import React, { useState, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  ClipboardCheck, Search, Filter, RefreshCw, AlertTriangle,
  TrendingDown, TrendingUp, CheckCircle2, Save, History,
  ArrowDown, ArrowUp, Calendar, User, FileText, ChevronRight
} from 'lucide-react';
import { useItems, useInventoryCategories, useAuditStock, useAudits } from '../hooks/useInventory';

const ADJUST_REASONS = [
  'Conteo físico / Cuadre',
  'Merma / Vencimiento',
  'Rotura / Deterioro',
  'Consumo interno / Degustación',
  'Error de registro previo',
  'Compra sin factura',
];

interface CountRow {
  itemId: number;
  name: string;
  sku: string;
  category: string;
  unit: string;
  cost: number;
  systemStock: number;
  countedStock: string; // string para permitir campo vacío
  reason: string;
  notes: string;
}

export const PhysicalInventoryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'AUDIT' | 'HISTORY'>('AUDIT');
  const [selectedCategory, setSelectedCategory] = useState<string>('TODAS');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showOnlyDiscrepancies, setShowOnlyDiscrepancies] = useState<boolean>(false);
  const [auditNotes, setAuditNotes] = useState<string>('');
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [registerAsExpense, setRegisterAsExpense] = useState<boolean>(false);
  const [deductFromCash, setDeductFromCash] = useState<boolean>(false);
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');

  // Queries y Mutaciones
  const { data: itemsPage, isLoading: loadingItems, refetch: refetchItems } = useItems({ limit: 1000 });
  const { data: dbCategories = [] } = useInventoryCategories();
  const { data: auditsHistory = [], isLoading: loadingAudits } = useAudits();
  const auditStockMut = useAuditStock();

  const items = itemsPage?.data ?? [];

  // Mapear estado inicial de conteo de filas
  const [counts, setCounts] = useState<Record<number, { countedStock: string; reason: string; notes: string }>>({});

  // Inputs refs para navegación rápida por teclado (Enter / Flechas)
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // Filas preparadas para renderizar
  const rows: CountRow[] = useMemo(() => {
    return items.map((item) => {
      const id = Number(item.id);
      const current = counts[id];
      return {
        itemId: id,
        name: item.name,
        sku: item.sku || '',
        category: item.category || 'General',
        unit: item.unit || 'und',
        cost: Number(item.cost || 0),
        systemStock: Number((item as any).current_stock ?? 0),
        countedStock: current?.countedStock ?? '',
        reason: current?.reason ?? 'Conteo físico / Cuadre',
        notes: current?.notes ?? '',
      };
    });
  }, [items, counts]);

  // Filtrado de filas
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (selectedCategory !== 'TODAS' && r.category !== selectedCategory) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!r.name.toLowerCase().includes(q) && !r.sku.toLowerCase().includes(q)) return false;
      }
      if (showOnlyDiscrepancies) {
        if (r.countedStock === '') return false;
        const diff = Number(r.countedStock) - r.systemStock;
        if (Math.abs(diff) < 0.0001) return false;
      }
      return true;
    });
  }, [rows, selectedCategory, searchQuery, showOnlyDiscrepancies]);

  // Totales y Estadísticas del Conteo Actual
  const stats = useMemo(() => {
    let totalItemsCounted = 0;
    let itemsWithDiff = 0;
    let totalLoss = 0;
    let totalGain = 0;
    let netDifferenceCost = 0;

    rows.forEach((r) => {
      if (r.countedStock !== '') {
        totalItemsCounted++;
        const counted = parseFloat(r.countedStock) || 0;
        const diff = counted - r.systemStock;
        const diffCost = diff * r.cost;

        if (Math.abs(diff) > 0.0001) {
          itemsWithDiff++;
          netDifferenceCost += diffCost;
          if (diffCost > 0) {
            totalGain += diffCost;
          } else {
            totalLoss += Math.abs(diffCost);
          }
        }
      }
    });

    return {
      totalItemsCounted,
      itemsWithDiff,
      totalLoss,
      totalGain,
      netDifferenceCost,
      progressPct: rows.length > 0 ? Math.round((totalItemsCounted / rows.length) * 100) : 0,
    };
  }, [rows]);

  const handleCountChange = (itemId: number, value: string) => {
    setCounts((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        countedStock: value,
        reason: prev[itemId]?.reason || 'Conteo físico / Cuadre',
        notes: prev[itemId]?.notes || '',
      },
    }));
  };

  const handleReasonChange = (itemId: number, reason: string) => {
    setCounts((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        countedStock: prev[itemId]?.countedStock ?? '',
        reason,
        notes: prev[itemId]?.notes || '',
      },
    }));
  };

  // Navegación por teclado tipo Excel (Enter / Abajo -> siguiente fila)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentIndex: number) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextRow = filteredRows[currentIndex + 1];
      if (nextRow && inputRefs.current[nextRow.itemId]) {
        inputRefs.current[nextRow.itemId]?.focus();
        inputRefs.current[nextRow.itemId]?.select();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevRow = filteredRows[currentIndex - 1];
      if (prevRow && inputRefs.current[prevRow.itemId]) {
        inputRefs.current[prevRow.itemId]?.focus();
        inputRefs.current[prevRow.itemId]?.select();
      }
    }
  };

  // Rellenar automáticamente con el stock teórico los ítems no tocados
  const handleFillWithTheoretical = () => {
    const nextCounts = { ...counts };
    filteredRows.forEach((r) => {
      if (!nextCounts[r.itemId] || nextCounts[r.itemId].countedStock === '') {
        nextCounts[r.itemId] = {
          countedStock: String(r.systemStock),
          reason: 'Conteo físico / Cuadre',
          notes: '',
        };
      }
    });
    setCounts(nextCounts);
    toast.info('Se completó el conteo con el stock del sistema para los ítems visibles.');
  };

  // Asumir cero en ítems no contados (Ideal para Inventario Inicial / Limpiar negativos)
  const handleSetUncountedToZero = () => {
    if (!window.confirm('¿Deseas establecer en 0 (Agotado) todos los insumos visibles que aún NO has contado? Esta es la mejor forma de limpiar stocks negativos si estás empezando.')) return;
    
    const nextCounts = { ...counts };
    filteredRows.forEach((r) => {
      if (!nextCounts[r.itemId] || nextCounts[r.itemId].countedStock === '') {
        nextCounts[r.itemId] = {
          countedStock: '0',
          reason: 'Conteo inicial / Limpieza de negativos',
          notes: 'Ajuste masivo a cero',
        };
      }
    });
    setCounts(nextCounts);
    toast.info('Se asignó 0 a los insumos no contados.');
  };

  // Limpiar conteos
  const handleClearCounts = () => {
    if (window.confirm('¿Deseas reiniciar todos los valores contados en esta sesión?')) {
      setCounts({});
      toast.info('Valores de conteo reiniciados.');
    }
  };

  // Guardar Auditoría
  const handleSaveAudit = () => {
    const countedEntries = rows
      .filter((r) => r.countedStock !== '')
      .map((r) => ({
        inventory_item_id: r.itemId,
        counted_stock: parseFloat(r.countedStock) || 0,
        reason: r.reason,
        notes: r.notes,
      }));

    if (countedEntries.length === 0) {
      toast.warning('Ingresa el conteo de al menos un insumo antes de guardar.');
      return;
    }

    auditStockMut.mutate(
      {
        category: selectedCategory,
        notes: auditNotes || undefined,
        items: countedEntries,
        register_as_expense: registerAsExpense,
        deduct_from_cash: registerAsExpense ? deductFromCash : false,
        invoice_number: registerAsExpense && invoiceNumber.trim() ? invoiceNumber.trim() : undefined,
      },
      {
        onSuccess: (res) => {
          toast.success(res.message || '¡Toma física guardada y stock ajustado exitosamente!');
          setConfirmModalOpen(false);
          setCounts({});
          setAuditNotes('');
          setRegisterAsExpense(false);
          setDeductFromCash(false);
          setInvoiceNumber('');
          refetchItems();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || 'Error al guardar la toma de inventario.');
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Control de Existencias</p>
            <h1 className="text-3xl font-extrabold text-[color:var(--color-text)] flex items-center gap-3">
              <ClipboardCheck size={32} className="text-primary" /> Toma Física de Inventario
            </h1>
            <p className="mt-1 text-sm text-muted">
              Planilla de auditoría y cuadre rápido de stock físico con cálculo de descuadre monetario ($).
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('AUDIT')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition flex items-center gap-2 ${
                activeTab === 'AUDIT'
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-surface border border-divider text-muted hover:text-[color:var(--color-text)]'
              }`}
            >
              <ClipboardCheck size={16} /> Planilla de Conteo
            </button>
            <button
              onClick={() => setActiveTab('HISTORY')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition flex items-center gap-2 ${
                activeTab === 'HISTORY'
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-surface border border-divider text-muted hover:text-[color:var(--color-text)]'
              }`}
            >
              <History size={16} /> Historial de Auditorías
            </button>
          </div>
        </div>

        {activeTab === 'AUDIT' ? (
          <>
            {/* KPI Cards de la Sesión de Conteo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-divider bg-surface p-4 shadow-xs">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-primary" /> Progreso de Conteo
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-black text-[color:var(--color-text)]">
                    {stats.totalItemsCounted} <span className="text-xs font-semibold text-muted">/ {rows.length}</span>
                  </span>
                  <span className="text-xs font-bold text-primary">({stats.progressPct}%)</span>
                </div>
                <div className="w-full bg-surface-muted rounded-full h-1.5 mt-2 overflow-hidden">
                  <div className="bg-primary h-1.5 rounded-full transition-all duration-300" style={{ width: `${stats.progressPct}%` }} />
                </div>
              </div>

              <div className="rounded-2xl border border-divider bg-surface p-4 shadow-xs">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1.5">
                  <AlertTriangle size={13} className="text-amber-500" /> Descuadres Detectados
                </p>
                <span className="text-2xl font-black text-amber-600 dark:text-amber-400">
                  {stats.itemsWithDiff} <span className="text-xs font-semibold text-muted">insumos</span>
                </span>
                <p className="text-[10px] text-muted mt-1">Con diferencia física vs sistema</p>
              </div>

              <div className="rounded-2xl border border-divider bg-surface p-4 shadow-xs">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1.5">
                  <TrendingDown size={13} className="text-red-500" /> Pérdida por Faltante
                </p>
                <span className="text-2xl font-black text-red-600 dark:text-red-400">
                  -${stats.totalLoss.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </span>
                <p className="text-[10px] text-muted mt-1">Valor de insumos faltantes</p>
              </div>

              <div className="rounded-2xl border border-divider bg-surface p-4 shadow-xs">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1.5">
                  <TrendingUp size={13} className="text-emerald-500" /> Descuadre Neto ($)
                </p>
                <span className={`text-2xl font-black ${stats.netDifferenceCost >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {stats.netDifferenceCost >= 0 ? '+' : '-'}${Math.abs(stats.netDifferenceCost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </span>
                <p className="text-[10px] text-muted mt-1">Balance total de la auditoría</p>
              </div>
            </div>

            {/* Barra de Filtros y Herramientas Rápidas */}
            <div className="rounded-2xl border border-divider bg-surface p-4 shadow-xs flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[280px]">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    type="text"
                    placeholder="Buscar por insumo o código..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-xl border border-divider bg-surface-muted pl-9 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Filter size={14} className="text-muted" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="rounded-xl border border-divider bg-surface-muted px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary font-medium"
                  >
                    <option value="TODAS">Todas las Categorías</option>
                    {dbCategories.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-2 text-xs font-semibold text-muted cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showOnlyDiscrepancies}
                    onChange={(e) => setShowOnlyDiscrepancies(e.target.checked)}
                    className="rounded border-divider text-primary focus:ring-primary h-4 w-4"
                  />
                  Solo con descuadre
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleSetUncountedToZero}
                  className="px-3 py-2 rounded-xl text-xs font-semibold border border-red-500/30 bg-red-500/10 text-red-600 hover:bg-red-500/20 transition"
                  title="Poner en 0 todos los insumos no contados (Limpia negativos)"
                >
                  Asumir todo 0
                </button>
                <button
                  type="button"
                  onClick={handleFillWithTheoretical}
                  className="px-3 py-2 rounded-xl text-xs font-semibold border border-divider hover:bg-surface-muted transition text-muted hover:text-[color:var(--color-text)]"
                  title="Llena los campos vacíos con el stock actual del sistema"
                >
                  Rellenar teóricos
                </button>
                <button
                  type="button"
                  onClick={handleClearCounts}
                  className="px-3 py-2 rounded-xl text-xs font-semibold border border-divider hover:bg-surface-muted transition text-muted hover:text-red-500"
                >
                  Limpiar
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmModalOpen(true)}
                  disabled={stats.totalItemsCounted === 0 || auditStockMut.isPending}
                  className="px-5 py-2.5 rounded-xl bg-primary text-white text-xs font-bold shadow-sm transition hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save size={15} /> Aplicar Inventario ({stats.totalItemsCounted})
                </button>
              </div>
            </div>

            {/* Planilla de Conteo Físico (Sheet-to-Shelf) */}
            <div className="rounded-2xl border border-divider bg-surface shadow-xs overflow-hidden">
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead className="bg-surface-muted text-muted font-bold text-[10px] uppercase sticky top-0 z-10 border-b border-divider">
                    <tr>
                      <th className="py-3 px-4">Insumo</th>
                      <th className="py-3 px-2">Categoría</th>
                      <th className="py-3 px-2">Unidad</th>
                      <th className="py-3 px-2 text-right">Costo Unit.</th>
                      <th className="py-3 px-3 text-right">Stock Sistema</th>
                      <th className="py-3 px-3 text-center w-36">Conteo Físico</th>
                      <th className="py-3 px-3 text-right">Diferencia</th>
                      <th className="py-3 px-3 text-right">Impacto ($)</th>
                      <th className="py-3 px-3 w-48">Motivo Descuadre</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider/50">
                    {loadingItems ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-muted">
                          <RefreshCw className="animate-spin mx-auto mb-2 text-primary" size={24} />
                          Cargando catálogo de inventario...
                        </td>
                      </tr>
                    ) : filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-muted">
                          No se encontraron insumos con los filtros actuales.
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map((row, idx) => {
                        const hasCount = row.countedStock !== '';
                        const countedNum = hasCount ? (parseFloat(row.countedStock) || 0) : null;
                        const diff = countedNum !== null ? countedNum - row.systemStock : null;
                        const diffCost = diff !== null ? diff * row.cost : null;
                        const isMismatch = diff !== null && Math.abs(diff) > 0.0001;

                        return (
                          <tr
                            key={row.itemId}
                            className={`transition hover:bg-surface-muted/60 ${
                              isMismatch ? (diff! < 0 ? 'bg-red-50/40 dark:bg-red-950/20' : 'bg-emerald-50/40 dark:bg-emerald-950/20') : ''
                            }`}
                          >
                            {/* Insumo */}
                            <td className="py-2.5 px-4 font-semibold text-[color:var(--color-text)]">
                              <div>{row.name}</div>
                              {row.sku && <span className="text-[10px] text-muted font-mono">{row.sku}</span>}
                            </td>

                            {/* Categoría */}
                            <td className="py-2.5 px-2 text-muted">
                              <span className="px-2 py-0.5 rounded-md bg-surface-muted text-[10px] font-medium border border-divider">
                                {row.category}
                              </span>
                            </td>

                            {/* Unidad */}
                            <td className="py-2.5 px-2 font-mono text-muted">
                              {row.unit}
                            </td>

                            {/* Costo Unitario */}
                            <td className="py-2.5 px-2 text-right font-mono text-muted">
                              ${row.cost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </td>

                            {/* Stock Teórico del Sistema */}
                            <td className="py-2.5 px-3 text-right font-bold text-muted font-mono">
                              {row.systemStock.toFixed(2)}
                            </td>

                            {/* Input Conteo Físico Real */}
                            <td className="py-2 px-3">
                              <input
                                ref={(el) => { inputRefs.current[row.itemId] = el; }}
                                type="number"
                                step="any"
                                min="0"
                                value={row.countedStock}
                                onChange={(e) => handleCountChange(row.itemId, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, idx)}
                                placeholder="—"
                                className={`w-full py-1.5 px-2 rounded-xl text-center font-bold text-sm border focus:outline-none focus:ring-2 focus:ring-primary ${
                                  hasCount
                                    ? isMismatch
                                      ? 'border-amber-500 bg-surface'
                                      : 'border-emerald-500 bg-emerald-50/20'
                                    : 'border-divider bg-surface-muted'
                                }`}
                              />
                            </td>

                            {/* Diferencia en Unidades */}
                            <td className="py-2.5 px-3 text-right font-mono font-bold">
                              {diff !== null ? (
                                <span className={isMismatch ? (diff < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400') : 'text-muted'}>
                                  {diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Impacto Monetario ($) */}
                            <td className="py-2.5 px-3 text-right font-mono font-bold">
                              {diffCost !== null ? (
                                <span className={diffCost < 0 ? 'text-red-600 dark:text-red-400' : diffCost > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted'}>
                                  {diffCost < 0 ? '-' : diffCost > 0 ? '+' : ''}${Math.abs(diffCost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Motivo del Descuadre */}
                            <td className="py-2 px-3">
                              {isMismatch ? (
                                <select
                                  value={row.reason}
                                  onChange={(e) => handleReasonChange(row.itemId, e.target.value)}
                                  className="w-full text-[11px] py-1 px-2 rounded-lg border border-divider bg-surface focus:outline-none focus:ring-1 focus:ring-primary"
                                >
                                  {ADJUST_REASONS.map((reason) => (
                                    <option key={reason} value={reason}>{reason}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-[10px] text-muted">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          /* Pestaña: Historial de Auditorías de Inventario */
          <div className="rounded-2xl border border-divider bg-surface shadow-xs p-6 space-y-4">
            <h2 className="text-lg font-bold text-[color:var(--color-text)]">
              Historial de Tomas Físicas de Inventario
            </h2>

            {loadingAudits ? (
              <div className="py-12 text-center text-muted">
                <RefreshCw className="animate-spin mx-auto mb-2 text-primary" size={24} />
                Cargando historial de auditorías...
              </div>
            ) : auditsHistory.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted">
                <History size={40} className="mx-auto mb-2 text-muted/40" />
                No hay auditorías registradas todavía. Realiza la primera desde la pestaña "Planilla de Conteo".
              </div>
            ) : (
              <div className="divide-y divide-divider/50 border border-divider rounded-xl overflow-hidden">
                {auditsHistory.map((audit) => (
                  <div key={audit.id} className="p-4 hover:bg-surface-muted/40 transition flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-sm text-primary">{audit.audit_code}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-surface-muted border border-divider">
                          {audit.category === 'ALL' ? 'Inventario Completo' : audit.category}
                        </span>
                      </div>
                      <div className="text-xs text-muted flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar size={13} /> {new Date(audit.created_at).toLocaleString()}
                        </span>
                        {audit.user && (
                          <span className="flex items-center gap-1">
                            <User size={13} /> {audit.user.name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6 text-xs">
                      <div>
                        <span className="text-muted block text-[10px] uppercase">Ítems Contados</span>
                        <span className="font-bold">{audit.total_items_counted} insumos</span>
                      </div>
                      <div>
                        <span className="text-muted block text-[10px] uppercase">Con Descuadre</span>
                        <span className="font-bold text-amber-600">{audit.items_with_discrepancy}</span>
                      </div>
                      <div className="text-right min-w-[100px]">
                        <span className="text-muted block text-[10px] uppercase">Descuadre Neto</span>
                        <span className={`font-mono font-bold ${audit.net_cost_difference >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {audit.net_cost_difference >= 0 ? '+' : '-'}${Math.abs(audit.net_cost_difference).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal de Confirmación y Cierre de Auditoría */}
        {confirmModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
            <div className="w-full max-w-lg rounded-3xl bg-surface border border-divider p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-2xl bg-primary/10 text-primary">
                  <ClipboardCheck size={26} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[color:var(--color-text)]">
                    Confirmar Toma Física de Inventario
                  </h3>
                  <p className="text-xs text-muted">
                    El sistema ajustará el stock teórico para que coincida con el conteo físico.
                  </p>
                </div>
              </div>

              {/* Resumen Financiero */}
              <div className="rounded-2xl bg-surface-muted p-4 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted">Total de insumos a cuadrar:</span>
                  <strong className="text-[color:var(--color-text)]">{stats.totalItemsCounted} insumos</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Insumos con descuadre:</span>
                  <strong className="text-amber-600">{stats.itemsWithDiff}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Pérdida por faltante:</span>
                  <strong className="text-red-500">-${stats.totalLoss.toLocaleString()}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Sobrante valorizado:</span>
                  <strong className="text-emerald-500">+${stats.totalGain.toLocaleString()}</strong>
                </div>
                <div className="border-t border-divider pt-2 flex justify-between text-sm font-bold">
                  <span>Ajuste Neto a Inventario:</span>
                  <span className={stats.netDifferenceCost >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                    {stats.netDifferenceCost >= 0 ? '+' : '-'}${Math.abs(stats.netDifferenceCost).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Registro inteligente de mercancía sobrante como gasto / compra */}
              {stats.totalGain > 0 && (
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={registerAsExpense}
                      onChange={(e) => {
                        setRegisterAsExpense(e.target.checked);
                        if (!e.target.checked) setDeductFromCash(false);
                      }}
                      className="mt-0.5 rounded border-gray-300 text-primary focus:ring-primary w-4 h-4 cursor-pointer"
                    />
                    <div>
                      <span className="text-xs font-bold text-[color:var(--color-text)] block">
                        Registrar sobrante (+${stats.totalGain.toLocaleString()}) como Gasto / Compra no registrada
                      </span>
                      <span className="text-[11px] text-muted block mt-0.5">
                        Si compraste estos insumos pero no alcanzaste a ingresar la factura al sistema, se creará la orden de compra automáticamente para reflejar el gasto en el balance financiero.
                      </span>
                    </div>
                  </label>

                  {registerAsExpense && (
                    <div className="pl-7 space-y-3 pt-2 border-t border-emerald-500/20">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deductFromCash}
                          onChange={(e) => setDeductFromCash(e.target.checked)}
                          className="rounded border-gray-300 text-primary focus:ring-primary w-4 h-4 cursor-pointer"
                        />
                        <span className="text-xs font-semibold text-[color:var(--color-text)]">
                          Descontar el dinero de la Caja Activa actual (Salida de Efectivo)
                        </span>
                      </label>

                      <div>
                        <label className="block text-[11px] font-medium text-muted mb-1">
                          N° Factura / Remisión / Recibo de compra (opcional)
                        </label>
                        <input
                          type="text"
                          value={invoiceNumber}
                          onChange={(e) => setInvoiceNumber(e.target.value)}
                          placeholder="Ej: FAC-9842 o Remisión de verdulería"
                          className="w-full rounded-xl border border-divider bg-surface px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-muted mb-1">
                  Notas de la auditoría (opcional)
                </label>
                <textarea
                  value={auditNotes}
                  onChange={(e) => setAuditNotes(e.target.value)}
                  placeholder="Ej: Cuadre de fin de mes, turno noche. Responsable: Jefe de cocina."
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary h-20"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setConfirmModalOpen(false)}
                  disabled={auditStockMut.isPending}
                  className="px-4 py-2.5 rounded-xl border border-divider text-xs font-bold hover:bg-surface-muted transition"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveAudit}
                  disabled={auditStockMut.isPending}
                  className="px-6 py-2.5 rounded-xl bg-primary text-white text-xs font-bold shadow-md transition hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
                >
                  {auditStockMut.isPending ? 'Aplicando ajuste...' : 'Confirmar y Ajustar Stock'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PhysicalInventoryPage;
